import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import './Modal.css';

export function Modal({ title, children, footer, onClose, className = '', busy = false }: {
  title: string; children: ReactNode; footer?: ReactNode; onClose: () => void; className?: string; busy?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const busyRef = useRef(busy);
  busyRef.current = busy;
  const close = useRef(onClose);
  close.current = onClose;
  const titleID = useId();
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    ref.current?.focus();
    const keydown = (e: KeyboardEvent) => {
      const overlays = document.querySelectorAll('.app-modal-overlay');
      if (overlays[overlays.length - 1] !== ref.current?.parentElement) return;
      if (e.key === 'Escape' && !busyRef.current) { e.preventDefault(); e.stopPropagation(); close.current(); }
      if (e.key === 'Tab') {
        const nodes = Array.from(ref.current?.querySelectorAll<HTMLElement>('button:not(:disabled),a[href],input:not(:disabled),textarea:not(:disabled),[tabindex="0"]') || []).filter(el => el.getClientRects().length);
        const first = nodes[0], last = nodes[nodes.length - 1];
        if (!first) { e.preventDefault(); return; }
        if (e.shiftKey && (document.activeElement === first || document.activeElement === ref.current)) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && (document.activeElement === last || document.activeElement === ref.current)) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', keydown);
    return () => { document.body.style.overflow = overflow; document.removeEventListener('keydown', keydown); previous?.focus(); };
  }, []);
  return createPortal(
    <div className="app-modal-overlay" onMouseDown={e => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div ref={ref} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={titleID} className={'app-modal ' + className}>
        <header><h3 id={titleID}>{title}</h3><button className="icon-btn" aria-label="关闭弹窗" disabled={busy} onClick={onClose}><X size={18} /></button></header>
        <div className="app-modal-body">{children}</div>
        {footer && <footer>{footer}</footer>}
      </div>
    </div>, document.body,
  );
}
